'use client'

import { useState } from 'react'
import { MapPin, Phone, Mail, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useCreateContactMutation } from '@/services/contacts/queries'

export default function ContactSection() {
    const [selectedLocation, setSelectedLocation] = useState('hcm')

    const createContactMutation = useCreateContactMutation()
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        message: ''
    })

    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', content: string } | null>(null)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))

        if (statusMessage) setStatusMessage(null)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setStatusMessage(null)

        const siteId = Number(process.env.NEXT_PUBLIC_NEWS_SITE_KEY) || 0

        createContactMutation.mutate({
            siteId: siteId,
            fullName: formData.fullName,
            email: formData.email,
            message: formData.message,
            status: 0
        }, {
            onSuccess: (data) => {
                if (data.isOK) {
                    setStatusMessage({ type: 'success', content: 'Gửi tin nhắn thành công!' })
                    setFormData({ fullName: '', email: '', message: '' })
                } else {
                    const errorContent = data.errorMessages?.map((err: any) => {
                        if (typeof err === 'object' && err !== null) {
                            return err.message || err.errorMessage || JSON.stringify(err);
                        }
                        return String(err);
                    }).join(', ') || 'Có lỗi xảy ra, vui lòng kiểm tra lại thông tin.';

                    setStatusMessage({
                        type: 'error',
                        content: errorContent
                    })
                }
            },
            onError: (error) => {
                console.error(error)
                setStatusMessage({ type: 'error', content: 'Lỗi kết nối, vui lòng thử lại sau.' })
            }
        })
    }

    const locations = {
        hcm: {
            name: 'Trụ sở',
            address: 'Toà nhà Rovi Holdings - Số 4 Hát Giang, phường Tân Sơn Hoà, Tp. Hồ Chí Minh',
            lat: 10.8125,
            lng: 106.6675,
        },
        cantho: {
            name: 'Chi nhánh Cần Thơ',
            address: '30 Trần Văn Trà, KDC Hưng Phú 1, P. Hưng Phú, Thành Phố Cần Thơ',
            lat: 10.0167,
            lng: 105.7854,
        },
    }

    const currentLocation = locations[selectedLocation as keyof typeof locations]

    return (
        <section className="w-full py-4 md:py-8 px-6 md:px-6 lg:px-8">
            <div className="max-w-[52rem] mx-auto space-y-3 md:space-y-5 md:border md:border-gray-200 md:rounded-xl md:p-3 md:bg-[#FFFFFF] md:shadow-xl">

                {/* === Map & Info Wrapper === */}
                <div className="relative w-full h-auto md:h-[260px] md:rounded-lg md:overflow-hidden md:border md:border-gray-100 group flex flex-col md:block">

                    {/* KHUNG CHỌN VỊ TRÍ (INFO CARD) */}
                    <div className="order-1 relative w-full mb-0 flex flex-col items-center justify-center 
                                    bg-white border border-gray-200 border-b-0 shadow-sm rounded-t-lg rounded-b-none p-4
                                    md:absolute md:z-10 md:bottom-4 md:right-4 md:mb-0 md:w-[300px] md:items-start md:bg-white md:rounded-md md:shadow-xl md:p-3 md:border-0 md:animate-in md:fade-in md:slide-in-from-bottom-4 md:duration-700">

                        <div className="flex flex-col items-center md:items-start space-y-2 w-full" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                            <img
                                src="/images/Contact/ROVIHOLDINGS.png"
                                alt="Rovi Holdings"
                                className="h-9 w-auto object-contain md:-mt-3"
                            />
                            <h3 className="text-sm font-semibold text-foreground leading-snug text-[#333333] text-center md:text-left">
                                Công ty cổ phần đầu tư <br /> Rovi Holding
                            </h3>

                            {/* Nút chọn địa điểm */}
                            <div className="flex gap-2 w-full mt-3 md:mt-7 justify-center md:justify-start">
                                <Button
                                    onClick={() => setSelectedLocation('hcm')}
                                    size="sm"
                                    className={`rounded-md px-3 text-[10px] font-semibold h-7 transition-all ${selectedLocation === 'hcm'
                                        ? 'bg-[#006633] hover:bg-green-800 text-[#FFFFFF] shadow'
                                        : 'bg-white border border-gray-200 text-[#666666] hover:bg-gray-50'
                                        }`}
                                >
                                    Trụ sở
                                </Button>
                                <Button
                                    onClick={() => setSelectedLocation('cantho')}
                                    size="sm"
                                    className={`rounded-md px-3 text-[10px] font-semibold h-7 transition-all ${selectedLocation === 'cantho'
                                        ? 'bg-[#006633] hover:bg-[#006633] text-[#FFFFFF] shadow'
                                        : 'bg-white border border-gray-200 text-[#666666] hover:bg-gray-50'
                                        }`}
                                >
                                    Chi nhánh Cần Thơ
                                </Button>
                            </div>
                            <div className="pt-2 w-full text-center md:text-left border-t border-gray-100 md:border-none mt-2 md:mt-0">
                                <p className="text-[10px] text-[#333333] leading-relaxed line-clamp-2">
                                    {currentLocation.address}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Phần Map */}
                    <div className="order-2 relative w-full h-[200px] rounded-b-lg rounded-t-none overflow-hidden border border-gray-100 md:absolute md:inset-0 md:h-full md:rounded-none md:border-0 md:z-0">
                        <iframe
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            allowFullScreen={true}
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://maps.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}&hl=vi&z=16&output=embed`}
                            className="grayscale-[20%] group-hover:grayscale-0 transition-all duration-500"
                        />
                    </div>
                </div>

                {/* === Contact & Support Grid === */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>

                    {/* Column 1: Hotline & Email */}
                    <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-100 flex flex-col md:min-h-[340px]">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Phone className="w-4 h-4 text-green-700" />
                                <h3 className="text-sm font-medium text-[#333333] text-foreground">Hotline hỗ trợ</h3>
                            </div>
                            <p className="text-[11px] text-[#666666] mb-1">Liên hệ trực tiếp Tổng đài CSKH để nhận được hỗ trợ nhanh chóng</p>
                            <p className="text-sm font-semibold text-[#006633]">1900 2681</p>
                        </div>
                        <div className="w-full h-px bg-[#F0F0F0] my-4 md:my-8"></div>
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Mail className="w-4 h-4 text-green-700" />
                                <h3 className="text-sm font-medium text-[#333333] text-foreground">Email</h3>
                            </div>
                            <p className="text-[11px] text-[#666666] mb-1">Gửi thư điện tử để nhận hỗ trợ hoặc liên hệ hợp tác với Rovi Holding</p>
                            <p className="text-sm font-semibold text-[#006633]">info@roviholdings.vn</p>
                        </div>
                    </div>

                    {/* Column 2: Online Support */}
                    <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-100 flex flex-col md:min-h-[340px]">
                        <h3 className="text-sm font-medium text-[#333333] text-foreground mb-2">Hỗ trợ trực tuyến</h3>
                        <p className="text-[11px] text-[#666666] mb-4 md:mb-6">Kết nối với Rovi Holding trực tiếp trên các nền tảng mạng xã hội</p>
                        <div className="flex flex-col">
                            <SocialLink href="https://www.facebook.com/roviholdings" icon="/images/Contact/fb.png" label="Fanpage Rovi Holding" />
                            <div className="w-full border-t border-[#F0F0F0]"></div>
                            <SocialLink href="https://www.linkedin.com/company/rovi-holdings" icon="/images/Contact/in.png" label="LinkedIn" />
                            <div className="w-full border-t border-[#F0F0F0]"></div>
                            <SocialLink href="https://zalo.me/roviholdings" icon="/images/Contact/Zalo.png" label="Liên hệ Zalo" />
                        </div>
                    </div>

                    {/* Column 3: Contact Form */}
                    <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-100 flex flex-col justify-center md:min-h-[340px]">
                        <h3 className="text-sm font-medium text-foreground mb-2">Để lại tin nhắn</h3>
                        <p className="text-[11px] text-[#666666] mb-4">Chúng tôi sẽ liên hệ lại bạn sớm nhất</p>

                        <form className="space-y-3" onSubmit={handleSubmit}>
                            <div>
                                <p className="text-[10px] font-light text-[#9CA3AF] mb-1">Tên của bạn</p>
                                <Input
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    placeholder="Nhập tên của bạn"
                                    className="rounded-md h-7 px-3 text-[11px] bg-[#FFFFFF] border-gray-200 placeholder:text-[#9CA3AF] placeholder:text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
                                    required
                                />
                            </div>

                            <div>
                                <p className="text-[10px] font-light text-[#9CA3AF] mb-1">Email của bạn</p>
                                <Input
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Nhập email của bạn"
                                    className="rounded-md h-7 px-3 text-[11px] bg-[#FFFFFF] border-gray-200 placeholder:text-[#9CA3AF] placeholder:text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
                                    required
                                />
                            </div>

                            <div>
                                <p className="text-[10px] font-light text-[#9CA3AF] mb-1">Lời nhắn</p>
                                <Textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Để lại lời nhắn"
                                    rows={2}
                                    className="rounded-md min-h-[50px] py-2 text-[11px] bg-[#FFFFFF] border-gray-200 resize-none placeholder:text-[#9CA3AF] placeholder:text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
                                    required
                                />
                            </div>

                            {statusMessage && (
                                <div className={`text-[11px] font-medium ${statusMessage.type === 'success' ? 'text-green-600' : 'text-red-500'
                                    }`}>
                                    {statusMessage.content}
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-[#006633] hover:bg-red-700 text-white font-medium rounded-2xl"
                                disabled={createContactMutation.isPending}
                            >
                                {createContactMutation.isPending ? 'Đang gửi...' : 'Gửi thông tin'}
                                {!createContactMutation.isPending && <ChevronRight className="w-3 h-3 ml-2 text-[#FFFFFF]" />}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    )
}

function SocialLink({ href, icon, label }: { href: string; icon: string; label: string }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-2.5 rounded-md hover:bg-gray-50 transition group">
            <div className="flex items-center gap-1 -ml-2.5">
                <img src={icon} alt={label} className="w-8 h-8 object-contain" />
                <span className="text-xs font-semibold text-[#333333] group-hover:text-green-700 transition">{label}</span>
            </div>
            <svg className="w-2 h-2 text-[rgba(22,13,1,0.90)] group-hover:text-green-700 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
        </a>
    )
}